import { inject, Injectable, Injector, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GenericClass, transformObjectToPath, TranslationService } from '@pc/translation';
import enAU from '../i18n/en-AU';
import { map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class Translations extends GenericClass<typeof enAU>() {
  private translationService = inject(TranslationService);

  constructor() {
    super();
    Object.assign(this, transformObjectToPath<typeof enAU>('', enAU));
  }

  /**
   * Translates a given key from the translation resource and applies a mapping function to the result.
   * @param key - The key to be translated, corresponding to a property in the translation resource.
   * @param mapFn - A function that takes the translated value and maps it to a desired format or structure `T`. use TranslationValue<K> as the type of arg.
   * @param injector - (Optional) An Angular `Injector` instance for dependency injection, if needed.
   * @returns A Signal of type `T` that emits the transformed value whenever the translation changes.
   */
  public translateAndMap<K extends keyof typeof enAU, T>(
    key: K,
    mapFn: (arg: (typeof enAU)[K]) => T,
    injector?: Injector
  ): Signal<T> {
    return toSignal(
      this.translationService.translate.stream(key).pipe(
        map((labels) => {
          return mapFn(labels);
        })
      ),
      { initialValue: {} as T, injector }
    );
  }

  public instant(key: string, interpolateParams?: object): string {
    return this.translationService.instant(key, interpolateParams);
  }
}

export type TranslationValue<K extends keyof typeof enAU> = (typeof enAU)[K];
